import React from "react";

const PREFIX = "webprofit:list-view:";

function storageKey(key) {
  return `${PREFIX}${String(key || "default")}`;
}

function readValue(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(storageKey(key));
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
}

function writeValue(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(storageKey(key), JSON.stringify(value));
  } catch (_error) {
    // La persistencia es una mejora de UX; nunca debe bloquear el listado.
  }
}

export function clearPersistentListValue(key) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(storageKey(key));
  } catch (_error) {
    // noop
  }
}

export function usePersistentListValue(key, fallback) {
  const fallbackRef = React.useRef(fallback);
  const keyRef = React.useRef(key);
  const skipWriteRef = React.useRef(false);
  fallbackRef.current = fallback;
  const [value, setValue] = React.useState(() => readValue(key, fallback));

  React.useLayoutEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    skipWriteRef.current = true;
    setValue(readValue(key, fallbackRef.current));
  }, [key]);

  React.useEffect(() => {
    if (keyRef.current !== key) return;
    if (skipWriteRef.current) {
      skipWriteRef.current = false;
      return;
    }
    writeValue(key, value);
  }, [key, value]);

  return [value, setValue];
}

export function useListScrollRestoration(key, ready = true) {
  const restoredRef = React.useRef("");

  React.useEffect(() => {
    if (!ready || typeof window === "undefined" || restoredRef.current === key) return undefined;
    restoredRef.current = key;
    const saved = Number(readValue(`${key}:scroll`, 0));
    if (!Number.isFinite(saved) || saved <= 0) return undefined;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.scrollTo({ top: saved, left: 0, behavior: "auto" }));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [key, ready]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    return () => writeValue(`${key}:scroll`, window.scrollY || 0);
  }, [key]);
}
